import { openDB, IDBPDatabase } from 'idb'
import { CONFIG } from '../config'
import { encrypt, decrypt } from './crypto'

interface CacheEntry {
  vaultId: string
  encryptedData: Uint8Array
  timestamp: number
  size: number
}

interface CacheDB {
  vaults: CacheEntry
}

let dbPromise: Promise<IDBPDatabase<CacheDB>> | null = null

function getDB(): Promise<IDBPDatabase<CacheDB>> {
  if (!dbPromise) {
    dbPromise = openDB<CacheDB>(CONFIG.CACHE_DB_NAME, CONFIG.CACHE_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('vaults')) {
          db.createObjectStore('vaults', { keyPath: 'vaultId' })
        }
      },
    })
  }
  return dbPromise
}

// Store encrypted vault data in local cache
export async function cacheVaultData(
  vaultId: string,
  data: Uint8Array,
  cacheKey: Uint8Array
): Promise<void> {
  const db = await getDB()

  // Encrypt data for local storage
  const encryptedData = await encrypt(data, cacheKey)

  const entry: CacheEntry = {
    vaultId,
    encryptedData,
    timestamp: Date.now(),
    size: encryptedData.length,
  }

  await db.put('vaults', entry)

  // Enforce cache size limit
  await enforceCacheSizeLimit()
}

// Retrieve cached vault data
export async function getCachedVaultData(
  vaultId: string,
  cacheKey: Uint8Array
): Promise<Uint8Array | null> {
  const db = await getDB()
  const entry = await db.get('vaults', vaultId)

  if (!entry) return null

  try {
    return await decrypt(entry.encryptedData, cacheKey)
  } catch {
    // Decryption failed - wrong key or corrupted data
    return null
  }
}

// Check if vault is cached
export async function isVaultCached(vaultId: string): Promise<boolean> {
  const db = await getDB()
  const entry = await db.get('vaults', vaultId)
  return !!entry
}

// Remove vault from cache
export async function removeCachedVault(vaultId: string): Promise<void> {
  const db = await getDB()
  await db.delete('vaults', vaultId)
}

// Get total cache size
export async function getCacheSize(): Promise<number> {
  const db = await getDB()
  const entries = await db.getAll('vaults')
  return entries.reduce((total, entry) => total + entry.size, 0)
}

// Enforce cache size limit (LRU eviction)
async function enforceCacheSizeLimit(): Promise<void> {
  const db = await getDB()
  let entries = await db.getAll('vaults')

  // Sort by timestamp (oldest first)
  entries.sort((a, b) => a.timestamp - b.timestamp)

  let totalSize = entries.reduce((sum, e) => sum + e.size, 0)

  // Remove oldest entries until under limit
  while (totalSize > CONFIG.LOCAL_CACHE_MAX_BYTES && entries.length > 0) {
    const oldest = entries.shift()!
    await db.delete('vaults', oldest.vaultId)
    totalSize -= oldest.size
  }
}

// Clear entire cache
export async function clearCache(): Promise<void> {
  const db = await getDB()
  await db.clear('vaults')
}

// Get cache info for display
export async function getCacheInfo(): Promise<{
  totalSize: number
  vaultCount: number
  maxSize: number
}> {
  const db = await getDB()
  const entries = await db.getAll('vaults')
  return {
    totalSize: entries.reduce((sum, e) => sum + e.size, 0),
    vaultCount: entries.length,
    maxSize: CONFIG.LOCAL_CACHE_MAX_BYTES,
  }
}
